import { useState, ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { createAdminWallet } from '@/api/stake-db';
import { CreateAdminWalletRequest } from '@/types/stake-db';

interface AdminWalletFormProps {
  onCreated?: () => void;
}

export default function AdminWalletForm({ onCreated }: AdminWalletFormProps) {
  const [formData, setFormData] = useState({
    address: '',
    privateKey: '',
    name: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.address) {
      toast.error('지갑 주소를 입력해주세요.');
      return;
    }
    if (!formData.privateKey) {
      toast.error('Private Key를 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const data: CreateAdminWalletRequest = {
        address: formData.address,
        privateKey: formData.privateKey,
        name: formData.name || undefined,
        description: formData.description || undefined,
      };

      const re = await createAdminWallet(data);

      if (re.status === 200 || re.status === 201) {
        toast.success(re.data.message || '관리자 지갑 등록 성공');
        setFormData({
          address: '',
          privateKey: '',
          name: '',
          description: '',
        });
        onCreated?.();
      } else {
        toast.error(re.data?.message || '관리자 지갑 등록 실패');
      }
    } catch (error) {
      console.error('Create admin wallet error:', error);
      toast.error('관리자 지갑 등록 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            관리자 지갑 등록
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stake-db 서비스에서 사용할 관리자 지갑을 등록합니다.
          </Typography>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Address</InputLabel>
            <OutlinedInput
              value={formData.address}
              label="Address"
              onChange={handleChange('address')}
              placeholder="0x..."
            />
          </FormControl>
        </Grid>

        <Grid xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Private Key</InputLabel>
            <OutlinedInput
              value={formData.privateKey}
              label="Private Key"
              onChange={handleChange('privateKey')}
              type="password"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Name</InputLabel>
            <OutlinedInput
              value={formData.name}
              label="Name"
              onChange={handleChange('name')}
              placeholder="지갑 이름 (선택)"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Description</InputLabel>
            <OutlinedInput
              value={formData.description}
              label="Description"
              onChange={handleChange('description')}
              placeholder="설명 (선택)"
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : '지갑 등록'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
